<script setup lang="ts">
  import Footer from '~renderer/components/Footer.vue'
  import Header from '~renderer/components/Header.vue'
</script>

<template>
  <div class="flex min-h-screen flex-col bg-purple-500 font-sans text-white">
    <Header />
    <main class="flex-1">
      <!-- Hero Section -->
      <section
        class="bg-base-sky relative overflow-hidden border-b border-black/10 py-16 text-center text-white"
      >
        <div class="relative mx-auto max-w-4xl px-4">
          <h1 class="font-display text-tan-100 mb-2 text-4xl font-bold drop-shadow-md md:text-5xl">
            Privacy Policy
          </h1>
          <p class="text-tan-300 text-sm font-medium tracking-wide uppercase">
            Last Updated: 30 April 2020
          </p>
        </div>
      </section>

      <!-- Main Content -->
      <div class="relative mx-auto max-w-4xl px-4 py-12">
        <div class="space-y-12">
          <!-- Introduction -->
          <section>
            <h2 class="text-tan-100 mb-4 text-2xl font-bold">Introduction</h2>
            <div class="text-tan-50 space-y-4 leading-relaxed">
              <p>
                This Privacy Policy applies to the turnip.exchange website (the “Website” or
                “Services”), which is owned and operated by Warp World, Inc., a Delaware Corporation
                only, and not to any other site, service, or software. This Privacy Policy explains
                what information we collect from and about you when you use our Services, including
                information which may identify you or be used to identify you (“Personal
                Information”) and other types of data (collectively, “Your Information”), and how we
                use it.
              </p>
              <p>
                Please read this Privacy Policy carefully – it is the baseline for how we handle
                Your Information. Remember that your use of the Services may be subject to other
                terms of use in addition to this Privacy Policy. This Privacy Policy covers our
                treatment of Your Information, but does not apply to the practices of companies we
                don’t own or control. If you have concerns about our data collection and use
                practices, as explained below, please do not use the Services.
              </p>
            </div>
          </section>

          <!-- Information We Collect Directly From You -->
          <section>
            <h2 class="text-tan-100 mb-4 text-2xl font-bold">
              Information We Collect Directly From You
            </h2>
            <div class="text-tan-50 space-y-4 leading-relaxed">
              <p>
                We collect some information directly from you when you choose to use our Services,
                such as your username. You may also have the opportunity to volunteer more
                information, for example your dodo code. In all these situations, you have the
                option of not providing us this information. If you do not provide all required
                information, you may not be able to take part in or use certain parts of our
                Services. We store this information only as long as necessary to provide you with
                the Services as described in this Privacy Policy, or to prevent fraud or abuse.
              </p>
            </div>
          </section>

          <!-- Information Collected Automatically -->
          <section>
            <h2 class="text-tan-100 mb-4 text-2xl font-bold">
              Information Collected Automatically
            </h2>
            <div class="text-tan-50 space-y-4 leading-relaxed">
              <p>
                We receive some information automatically when you visit our Website. This includes
                information about the device, browser, and operating system you use when accessing
                our site and Services, your IP address, the website that referred you to our
                Website, which pages you request and visit, and the date and time of each request
                you make to our Services.
              </p>
              <p>
                We sometimes partner with third-party services, such as Patreon, which may use
                various tracking technologies (including cookies) to provide certain services or
                features. These technologies allow a partner to recognize your computer or mobile
                device each time you visit our Website, and such information collected by our
                partners is subject to that partner’s own privacy policy. Some third parties may
                collect data from you in order to protect our site from fraud or other malicious
                activity. We utilize bot mitigation and DDoS protection services from Kasada
                (https://kasada.io) and Cloudflare (https://cloudflare.com).
              </p>
            </div>
          </section>

          <!-- Use of Cookies -->
          <section>
            <h2 class="text-tan-100 mb-4 text-2xl font-bold">Use of Cookies</h2>
            <div class="text-tan-50 space-y-4 leading-relaxed">
              <p>
                Cookies are another way we automatically collect data about you. If you use our
                Services, we may place a cookie for the purpose of being able to track which pages
                of the site you wish to view. We also use third-party cookies from our partners
                (such as Cloudflare). Most browsers include an option to clear existing cookies or
                reject new ones. If you prefer not to use any cookies, you can also opt out in some
                browsers by adjusting your browser settings. However, if you reject new cookies,
                portions of the Services may not function as intended.
              </p>
            </div>
          </section>

          <!-- Do Not Track -->
          <section>
            <h2 class="text-tan-100 mb-4 text-2xl font-bold">Do Not Track</h2>
            <div class="text-tan-50 space-y-4 leading-relaxed">
              <p>We currently do not support Do Not Track browser settings.</p>
            </div>
          </section>

          <!-- How We Use or Disclose Your Information -->
          <section>
            <h2 class="text-tan-100 mb-4 text-2xl font-bold">
              How We Use or Disclose Your Information
            </h2>
            <div class="text-tan-50 space-y-4 leading-relaxed">
              <p>
                Your Information is used where necessary to provide our Services. This includes
                being shared with other users. In addition, if we employ other companies and people
                to perform tasks on our behalf, we may share Your Information with them as needed to
                provide the Services to you. Unless we or our partner tell you differently, our
                partners do not have any right to use any Personal Information we share with them
                beyond what is necessary to assist us.
              </p>
              <p>
                We may use or share Your Information when necessary to keep the Services running and
                prevent abuse. We also collect some information to make sure the Services work
                properly, research new services, and to improve our users’ experience. This may
                include using Your Information internally for analytical purposes. We may contact
                you with specific questions about your user experience or how we can serve you
                better.
              </p>
              <p>
                Except as otherwise described in this Privacy Policy, we will only share Your
                Information with your informed consent, letting you know what information will be
                shared and with who we’re sharing it with. We do not sell your Personal Information
                to any third party, except as described in the Business Acquisition or Merger
                section. We may create new, de-identified and aggregated data sets from Your
                Information, which does not contain any of your Personal Information (for example:
                the number of people who visit our site in a month). We may share this aggregated
                and/or de-identified information with third parties for any business purpose.
              </p>
            </div>
          </section>

          <!-- Third Party Sites and Services -->
          <section>
            <h2 class="text-tan-100 mb-4 text-2xl font-bold">Third Party Sites and Services</h2>
            <div class="text-tan-50 space-y-4 leading-relaxed">
              <p>
                The Services connect together other third-party sites and services, including but
                not limited to: patreon.com. We do not own or operate any of these sites or
                services. This privacy policy does not cover Your Information once it is sent to
                those sites or services; each site or service has its own privacy policy which you
                should review.
              </p>
            </div>
          </section>

          <!-- Business Acquisition or Merger -->
          <section>
            <h2 class="text-tan-100 mb-4 text-2xl font-bold">Business Acquisition or Merger</h2>
            <div class="text-tan-50 space-y-4 leading-relaxed">
              <p>
                If we are acquired by a third party as a result of a transaction such as a merger,
                acquisition, sale, or if our assets are acquired by a third party in the event that
                we go out of business or enter bankruptcy, some or all of our assets, including Your
                Information, may be disclosed or transferred to a third party or parties in
                connection with the transaction.
              </p>
            </div>
          </section>

          <!-- Law Enforcement -->
          <section>
            <h2 class="text-tan-100 mb-4 text-2xl font-bold">Law Enforcement</h2>
            <div class="text-tan-50 space-y-4 leading-relaxed">
              <p>
                We will cooperate with government and law enforcement officials or private parties
                to enforce and comply with the law. We may disclose Your Information to government
                or law enforcement officials or private parties as we, in our sole discretion,
                believe necessary or appropriate: (i) to comply with law, regulation or valid legal
                process (including orders and subpoenas); or (ii) to protect our property, rights,
                and safety, and the property, rights, and safety of a third party or the public in
                general. If we are going to release Your Information, our policy is to provide you
                with notice unless we are prohibited from doing so by law or court order.
              </p>
            </div>
          </section>

          <!-- Data Security -->
          <section>
            <h2 class="text-tan-100 mb-4 text-2xl font-bold">Data Security</h2>
            <div class="text-tan-50 space-y-4 leading-relaxed">
              <p>
                We will use reasonable efforts to secure information submitted to us by our users,
                including using encryption to protect data transmitted to and from our Services.
                However, no data transmission over the internet is completely secure, so we cannot
                guarantee the absolute security of this data. You use the Services at your own risk,
                and are responsible for taking reasonable measures to secure your account (such as
                keeping your password secret).
              </p>
            </div>
          </section>

          <!-- Children Under 13 -->
          <section>
            <h2 class="text-tan-100 mb-4 text-2xl font-bold">Children Under 13</h2>
            <div class="text-tan-50 space-y-4 leading-relaxed">
              <p>
                Our Services are not directed to people under the age of 13. If you are under 13,
                you may not create an account with or submit any Personal Information to our
                Services. If we become aware that we have collected Personal Information from a
                child under 13, we will delete that information.
              </p>
            </div>
          </section>

          <!-- International Transfer -->
          <section>
            <h2 class="text-tan-100 mb-4 text-2xl font-bold">International Transfer</h2>
            <div class="text-tan-50 space-y-4 leading-relaxed">
              <p>
                Your Information may be transferred to and stored on, computers located outside of
                your state, province, country or other governmental jurisdiction where the privacy
                laws may not be the same as those in your jurisdiction. If you are located outside
                the United States and choose to provide Your Information to us, we may transfer Your
                Information to the United States for processing and storage there.
              </p>
            </div>
          </section>

          <!-- Changes to This Privacy Policy -->
          <section>
            <h2 class="text-tan-100 mb-4 text-2xl font-bold">Changes to This Privacy Policy</h2>
            <div class="text-tan-50 space-y-4 leading-relaxed">
              <p>
                We may revise this Privacy Policy from time to time. The most current version of the
                Privacy Policy will govern our use of your information and will always be at:
                https://turnip.exchange/privacy. If we make what we believe are major changes to
                this policy, we will provide notice on the Website for a reasonable amount of time
                before those changes take effect.
              </p>
            </div>
          </section>

          <!-- Contact -->
          <section>
            <h2 class="text-tan-100 mb-4 text-2xl font-bold">Contact</h2>
            <div class="text-tan-50 space-y-4 leading-relaxed">
              <p>
                The Services are owned and operated by Warp World, Inc. If you have questions about
                the Services, Your Information, or this privacy policy, please contact us at
                <a href="mailto:privacy@warp.world" class="text-tan-200 underline hover:text-white">
                  privacy@warp.world </a
                >.
              </p>
            </div>
          </section>
        </div>
      </div>
    </main>
    <Footer />
  </div>
</template>
